import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide5Mobile from "./HTLDSlide5Mobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide5Content, useSlide5, Seg } from "./slide5.data";

const accent = "#9b72dd";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
boxSizing: "border-box", overflow: "hidden",
},
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(155,114,221,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1, minHeight: 0,
display: "flex", gap: "clamp(16px,2vw,28px)",
padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
alignItems: "stretch", overflow: "hidden",
},
content: {
flex: 1, minWidth: "min(100%, 380px)",
display: "flex", flexDirection: "column",
justifyContent: "space-between",
},
panel: {
width: "clamp(220px,28vw,380px)", flexShrink: 0,
display: "flex", flexDirection: "column",
overflow: "hidden",
},
};


// ─── Memory Depth Canvas ──────────────────────────────────────────────────────
const MemoryDepthCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawFnRef = useRef<null | (() => void)>(null);
  const dtRef     = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const W = 420, H = 560;
    const c = pick(slide5Content).canvas; // slides remount on language change
  
    // ── DPR fix — crisp on retina ────────────────────────────────────────────
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
  
    const WY = 200;
    let frame = 0;
  
    // Twinkling starfield (replaces 12 static dots)
    const stars = Array.from({ length: 100 }, () => ({
      x: Math.random() * W, y: Math.random() * (WY - 8),
      r: 0.3 + Math.random() * 1.3,
      tw: Math.random() * Math.PI * 2, spd: 0.010 + Math.random() * 0.024,
    }));
  
    // Floating memory fragments in the water
    const memParticles = Array.from({ length: 38 }, () => ({
      x: Math.random() * W, y: WY + 10 + Math.random() * (H - WY - 20),
      r: 0.4 + Math.random() * 1.1,
      vx: (Math.random()-0.5)*0.10, vy: (Math.random()-0.5)*0.12,
      tw: Math.random() * Math.PI*2, tws: 0.008 + Math.random() * 0.016,
    }));
  
    interface Orb {
      x: number; baseX: number; y: number; r: number;
      sinkSpd: number; riseSpd: number;
      rising: boolean; riseTimer: number; maxTimer: number;
      alpha: number; wobble: number; wobbleSpd: number;
    }
    interface Burst {
      x: number; y: number; frame: number; orbR: number;
      sparks: { angle: number; speed: number; size: number }[];
    }
  
    const BURST_FRAMES = 52;
    const bursts: Burst[] = [];
  
    function spawnBurst(x: number, y: number, orbR: number) {
      bursts.push({
        x, y, frame: 0, orbR,
        sparks: Array.from({ length: 18 }, () => ({
          angle: Math.random() * Math.PI * 2,
          speed: 2.8 + Math.random() * 4.5,
          size:  0.8 + Math.random() * 2.2,
        })),
      });
    }
  
    const orbs: Orb[] = [
      { x:95,  baseX:95,  y:WY+50,  r:7, sinkSpd:0.18, riseSpd:1.4, rising:false, riseTimer:0,   maxTimer:320, alpha:0.75, wobble:0,   wobbleSpd:0.018 },
      { x:200, baseX:200, y:WY+110, r:5, sinkSpd:0.12, riseSpd:1.8, rising:false, riseTimer:80,  maxTimer:280, alpha:0.60, wobble:1.2, wobbleSpd:0.022 },
      { x:320, baseX:320, y:WY+160, r:8, sinkSpd:0.20, riseSpd:1.2, rising:false, riseTimer:160, maxTimer:360, alpha:0.55, wobble:2.4, wobbleSpd:0.016 },
      { x:150, baseX:150, y:WY+240, r:6, sinkSpd:0.10, riseSpd:1.6, rising:false, riseTimer:240, maxTimer:400, alpha:0.42, wobble:3.6, wobbleSpd:0.020 },
      { x:290, baseX:290, y:WY+310, r:9, sinkSpd:0.15, riseSpd:1.0, rising:false, riseTimer:120, maxTimer:450, alpha:0.32, wobble:0.8, wobbleSpd:0.014 },
      { x:75,  baseX:75,  y:WY+390, r:5, sinkSpd:0.08, riseSpd:2.0, rising:false, riseTimer:200, maxTimer:500, alpha:0.25, wobble:4.2, wobbleSpd:0.024 },
      { x:355, baseX:355, y:WY+460, r:4, sinkSpd:0.06, riseSpd:1.5, rising:false, riseTimer:300, maxTimer:550, alpha:0.18, wobble:1.8, wobbleSpd:0.019 },
    ];
  
    const trails: { x: number; y: number; alpha: number; r: number }[] = [];
  
    function waveY(x: number, t: number): number {
      return WY + 5.5*Math.sin(x*0.038+t*0.022) + 2.8*Math.sin(x*0.072-t*0.016) + 1.2*Math.sin(x*0.12+t*0.030);
    }
  
    function draw() {
      ctx.clearRect(0, 0, W, H);
  
      // ── Sky ──────────────────────────────────────────────────────────────
      const sky = ctx.createLinearGradient(0, 0, 0, WY+30);
      sky.addColorStop(0, "#030c14"); sky.addColorStop(1, "#0c1828");
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, WY+30);
  
      // Sky nebulae
      ([
        [60,  50,  100, "rgba(80,40,160,0.16)"],
        [340, 65,   90, "rgba(50,20,140,0.13)"],
        [200, 30,  110, "rgba(70,30,155,0.12)"],
      ] as [number,number,number,string][]).forEach(([nx,ny,nr,nc]) => {
        const g = ctx.createRadialGradient(nx,ny,0,nx,ny,nr);
        g.addColorStop(0, nc); g.addColorStop(1,"rgba(0,0,0,0)");
        ctx.beginPath(); ctx.arc(nx,ny,nr,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
      });
  
      // Twinkling stars
      stars.forEach(s => {
        const tw = 0.25 + 0.75*Math.abs(Math.sin(frame*s.spd+s.tw));
        ctx.beginPath(); ctx.arc(s.x,s.y,s.r,0,Math.PI*2);
        ctx.fillStyle=`rgba(255,255,255,${(tw*0.72).toFixed(2)})`; ctx.fill();
      });
  
      // Moon with halos
      [[20,0.07],[13,0.13],[9,1]].forEach(([r,a],i) => {
        ctx.beginPath(); ctx.arc(385,36,r,0,Math.PI*2);
        ctx.fillStyle = i<2 ? `rgba(255,252,180,${a})` : "#fffde7"; ctx.fill();
      });
  
      // ── Deep water ───────────────────────────────────────────────────────
      const deep = ctx.createLinearGradient(0, WY, 0, H);
      deep.addColorStop(0,   "#091018");
      deep.addColorStop(0.3, "#060c14");
      deep.addColorStop(0.6, "#04080f");
      deep.addColorStop(1,   "#010305");
      ctx.fillStyle = deep; ctx.fillRect(0, WY, W, H-WY);
  
      // Underwater nebulae — depth atmosphere
      ([
        [W*0.28, WY+140, 130, "rgba(155,114,221,0.11)"],
        [W*0.72, WY+260, 110, "rgba(130,90,210,0.09)" ],
        [W*0.18, WY+390, 140, "rgba(155,114,221,0.08)"],
        [W*0.82, WY+430, 120, "rgba(120,80,200,0.07)" ],
      ] as [number,number,number,string][]).forEach(([nx,ny,nr,nc]) => {
        const g = ctx.createRadialGradient(nx,ny,0,nx,ny,nr);
        g.addColorStop(0, nc); g.addColorStop(1,"rgba(0,0,0,0)");
        ctx.beginPath(); ctx.arc(nx,ny,nr,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
      });
  
      // Subconscious central glow
      const subGlow = ctx.createRadialGradient(W/2,H*0.72,10,W/2,H*0.72,230);
      subGlow.addColorStop(0,"rgba(155,114,221,0.14)"); subGlow.addColorStop(1,"rgba(155,114,221,0)");
      ctx.beginPath(); ctx.rect(0,WY,W,H-WY); ctx.fillStyle=subGlow; ctx.fill();
  
      // Depth grid lines
      [WY+80,WY+180,WY+280,WY+380].forEach((ly,i) => {
        ctx.beginPath(); ctx.moveTo(0,ly); ctx.lineTo(W,ly);
        ctx.strokeStyle=`rgba(155,114,221,${0.08-i*0.012})`; ctx.lineWidth=1;
        ctx.setLineDash([6,14]); ctx.stroke(); ctx.setLineDash([]);
      });
  
      // ── Zone labels — boosted contrast ───────────────────────────────────
      ctx.font="bold 13px 'JetBrains Mono','Courier New',monospace"; ctx.textAlign="left"; ctx.textBaseline="alphabetic";
      ctx.fillStyle="rgba(210,190,255,0.88)"; ctx.fillText(c.conscious, 14, WY-14);
      ctx.fillStyle="rgb(185, 160, 255)"; ctx.fillText(c.subconscious, 14, WY+20);
  
      c.depths.forEach((label,i) => {
        const ly = WY+60+i*100;
        ctx.font="12px 'JetBrains Mono','Courier New',monospace";
        ctx.fillStyle=`white`; ctx.textAlign="right";
        ctx.fillText(label, W-8, ly);
        ctx.beginPath(); ctx.moveTo(W-55,ly-3); ctx.lineTo(W-60,ly-3);
        ctx.strokeStyle=`rgba(155,114,221,${0.35-i*0.05})`; ctx.lineWidth=1; ctx.stroke();
      });
  
      // ── Floating memory particles ─────────────────────────────────────────
      memParticles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.tw += p.tws;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < WY+5 || p.y > H-5) p.vy *= -1;
        const tw = 0.25 + 0.75*Math.abs(Math.sin(p.tw));
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2);
        ctx.fillStyle=`rgba(155,114,221,${(tw*0.25).toFixed(2)})`; ctx.fill();
      });
  
      // ── Trails ───────────────────────────────────────────────────────────
      for (let i = trails.length-1; i >= 0; i--) {
        trails[i].alpha -= 0.022;
        if (trails[i].alpha <= 0) { trails.splice(i,1); continue; }
        ctx.beginPath(); ctx.arc(trails[i].x,trails[i].y,trails[i].r,0,Math.PI*2);
        ctx.fillStyle=`rgba(155,114,221,${trails[i].alpha})`; ctx.fill();
      }
  
      // ── Orbs — enhanced with double glow layer ────────────────────────────
      const anyRising = orbs.some(o => o.rising);
      orbs.forEach((orb,idx) => {
        orb.wobble += orb.wobbleSpd;
        orb.x = orb.baseX + Math.sin(orb.wobble+idx*0.9)*9;
  
        if (!orb.rising) {
          orb.y += orb.sinkSpd*0.35;
          if (orb.y > H-15) { orb.y = WY+30; orb.alpha = 0.75-idx*0.07; }
          orb.riseTimer++;
          if (orb.riseTimer >= orb.maxTimer) { orb.rising=true; orb.riseTimer=0; }
        } else {
          orb.y -= orb.riseSpd;
          orb.alpha = Math.min(0.95, orb.alpha+0.008);
          if (Math.random() > 0.45)
            trails.push({ x:orb.x+(Math.random()-0.5)*6, y:orb.y+orb.r+4, alpha:0.42, r:1.5+Math.random()*1.5 });
          if (orb.y < WY-orb.r-4) {
            spawnBurst(orb.x, waveY(orb.x,frame)-orb.r-6, orb.r);
            orb.rising=false; orb.y=WY+35+idx*62; orb.alpha=0.75-idx*0.07;
            orb.riseTimer=0; orb.maxTimer=280+idx*60+Math.floor(Math.random()*120);
          }
        }
  
        const depthRatio   = Math.max(0,(orb.y-WY)/(H-WY-20));
        const displayAlpha = orb.rising ? orb.alpha : orb.alpha*(1-depthRatio*0.55);
  
        // Outer aura
        const aura = ctx.createRadialGradient(orb.x,orb.y,orb.r,orb.x,orb.y,orb.r+28);
        aura.addColorStop(0,`rgba(155,114,221,${displayAlpha*0.22})`); aura.addColorStop(1,"rgba(155,114,221,0)");
        ctx.beginPath(); ctx.arc(orb.x,orb.y,orb.r+28,0,Math.PI*2); ctx.fillStyle=aura; ctx.fill();
  
        // Inner glow
        const orbGrd = ctx.createRadialGradient(orb.x,orb.y,0,orb.x,orb.y,orb.r+14);
        orbGrd.addColorStop(0,`rgba(220,200,255,${displayAlpha*0.55})`);
        orbGrd.addColorStop(0.5,`rgba(155,114,221,${displayAlpha*0.42})`);
        orbGrd.addColorStop(1,"rgba(155,114,221,0)");
        ctx.beginPath(); ctx.arc(orb.x,orb.y,orb.r+14,0,Math.PI*2); ctx.fillStyle=orbGrd; ctx.fill();
  
        // Core
        ctx.beginPath(); ctx.arc(orb.x,orb.y,orb.r,0,Math.PI*2);
        ctx.fillStyle=`rgba(190,165,255,${displayAlpha})`; ctx.fill();
        ctx.strokeStyle=`rgba(235,220,255,${displayAlpha*0.90})`; ctx.lineWidth=1.2; ctx.stroke();
  
        // Specular
        ctx.beginPath(); ctx.arc(orb.x-orb.r*0.32,orb.y-orb.r*0.32,orb.r*0.36,0,Math.PI*2);
        ctx.fillStyle=`rgba(255,255,255,${displayAlpha*0.65})`; ctx.fill();
      });
  
      // ── Waterline waves ───────────────────────────────────────────────────
      // Fill
      ctx.beginPath(); ctx.moveTo(0,waveY(0,frame));
      for (let x=0;x<=W;x+=3) ctx.lineTo(x,waveY(x,frame));
      ctx.lineTo(W,WY+30); ctx.lineTo(0,WY+30); ctx.closePath();
      const waveFill = ctx.createLinearGradient(0,WY,0,WY+32);
      waveFill.addColorStop(0,"rgba(155,114,221,0.25)"); waveFill.addColorStop(1,"rgba(155,114,221,0)");
      ctx.fillStyle=waveFill; ctx.fill();
  
      // Wave strokes (3 layers)
      ([
        [0,   "rgba(210,185,255,0.80)", 2.0],
        [30,  "rgba(155,114,221,0.32)", 1.0],
        [65,  "rgba(155,114,221,0.16)", 0.7],
      ] as [number,string,number][]).forEach(([off,color,lw]) => {
        ctx.beginPath(); ctx.moveTo(0,waveY(0,frame+off));
        for (let x=0;x<=W;x+=3) ctx.lineTo(x,waveY(x,frame+off));
        ctx.strokeStyle=color; ctx.lineWidth=lw; ctx.stroke();
      });
  
      // Foam dots
      for (let x=10;x<W-10;x+=18+Math.sin(x*0.08+frame*0.04)*7) {
        const wy = waveY(x,frame);
        const fa = 0.18+0.12*Math.sin(x*0.14+frame*0.07);
        ctx.beginPath(); ctx.arc(x,wy,0.9+0.7*Math.sin(x*0.45+frame*0.1),0,Math.PI*2);
        ctx.fillStyle=`rgba(220,200,255,${fa})`; ctx.fill();
      }
  
      // ── Bursts ────────────────────────────────────────────────────────────
      for (let bi=bursts.length-1; bi>=0; bi--) {
        const b=bursts[bi], p=b.frame/BURST_FRAMES, inv=1-p;
  
        // Flash
        const flashP = Math.max(0,1-p*2.6);
        if (flashP>0) {
          const fR=b.orbR*(1+p*14);
          const fGrd=ctx.createRadialGradient(b.x,b.y,0,b.x,b.y,fR);
          fGrd.addColorStop(0,    `rgba(255,255,255,${flashP*0.98})`);
          fGrd.addColorStop(0.12, `rgba(245,235,255,${flashP*0.88})`);
          fGrd.addColorStop(0.30, `rgba(200,160,255,${flashP*0.65})`);
          fGrd.addColorStop(0.60, `rgba(155,114,221,${flashP*0.30})`);
          fGrd.addColorStop(1,    "rgba(155,114,221,0)");
          ctx.beginPath(); ctx.arc(b.x,b.y,fR,0,Math.PI*2); ctx.fillStyle=fGrd; ctx.fill();
        }
  
        // Spark particles (were defined but never drawn in original)
        b.sparks.forEach(spark => {
          const dist = spark.speed * p * BURST_FRAMES * 0.12;
          const sx = b.x + Math.cos(spark.angle)*dist;
          const sy = b.y + Math.sin(spark.angle)*dist;
          const sa = inv*0.75;
          if (sa>0.01) {
            ctx.beginPath(); ctx.arc(sx,sy,spark.size*inv,0,Math.PI*2);
            ctx.fillStyle=`rgba(220,200,255,${sa.toFixed(3)})`; ctx.fill();
          }
        });
  
        // Inner ring
        ctx.beginPath(); ctx.arc(b.x,b.y,b.orbR+p*48,0,Math.PI*2);
        ctx.strokeStyle=`rgba(220,200,255,${inv*0.85})`; ctx.lineWidth=2.8*inv; ctx.stroke();
  
        // Outer ring
        ctx.beginPath(); ctx.arc(b.x,b.y,b.orbR+p*80,0,Math.PI*2);
        ctx.strokeStyle=`rgba(155,114,221,${inv*0.40})`; ctx.lineWidth=1.6*inv; ctx.stroke();
  
        // Faint third ring
        if (p>0.15) {
          ctx.beginPath(); ctx.arc(b.x,b.y,b.orbR+(p-0.15)*110,0,Math.PI*2);
          ctx.strokeStyle=`rgba(155,114,221,${(inv*0.20).toFixed(3)})`; ctx.lineWidth=1; ctx.stroke();
        }
  
        b.frame++;
        if (b.frame>=BURST_FRAMES) bursts.splice(bi,1);
      }
  
      // ── Labels ────────────────────────────────────────────────────────────
      if (anyRising) {
        const la = 0.65+0.22*Math.sin(frame*0.06);
        ctx.font="bold 12px 'JetBrains Mono','Courier New',monospace";
        ctx.fillStyle=`rgba(200,175,255,${la})`; ctx.textAlign="center";
        ctx.fillText(c.resurfacing, W/2, WY-30);
      }
  
      ctx.font="15px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle="rgb(185, 155, 255)"; ctx.textAlign="center";
      ctx.fillText(c.caption, W/2, H-14);
  
      frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }
  
    drawFnRef.current = draw;
    return () => { drawFnRef.current = null; };
  }, []);
  
  useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });
  
  return (
    <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
      <canvas
        ref={canvasRef}
        style={{ display:"block", borderRadius:8, width:"100%", height:"100%", objectFit:"contain" }}
      />
    </div>
  );
  };

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide5Inner: React.FC = () => {
const { meta, journey, forgotten, gatekeeper, opportunity } = useSlide5();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
if (!existing) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}
return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = {
fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
color: accent, letterSpacing: 3, textTransform: "uppercase",
marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.headerTitlePre}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
    </div>
  </div>

  {/* Body */}
  <div style={S.body}>

    {/* Content — left column */}
    <div style={S.content}>

      {/* Title */}
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
          {meta.titlePre}<span style={{ color: accent }}>{meta.titleAccentWord}</span>{meta.titlePost1}<br/>{meta.titlePost2}
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0 }}>
          {meta.subtitle}
        </p>
      </div>

      {/* Nothing is truly forgotten */}
      <div style={{ borderBottom: "1px solid rgba(155,114,221,0.10)", paddingBottom: "clamp(6px,0.9vw,12px)" }}>
        <div style={lbl}>{meta.forgottenLabel}</div>
        <p style={txt}>{renderSegs(forgotten, "#d8c0ff")}</p>
      </div>

      {/* Gatekeeper */}
      <div style={{ borderBottom: "1px solid rgba(155,114,221,0.10)", paddingBottom: "clamp(6px,0.9vw,12px)" }}>
        <div style={lbl}>{meta.gatekeeperLabel}</div>
        <p style={txt}>{renderSegs(gatekeeper, "#d8c0ff")}</p>
      </div>

      {/* Journey of a memory — compact horizontal */}
      <div>
        <div style={lbl}>{meta.journeyLabel}</div>
        <div style={{ display: "flex", position: "relative" }}>
          <div style={{ position: "absolute", top: 8, left: 8, right: 8, height: 1, background: "rgba(155,114,221,0.18)" }} />
          {journey.map((j, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1, padding: "0 clamp(3px,0.5vw,6px)" }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", background: j.color, border: "1px solid rgba(155,114,221,0.55)", marginBottom: 6, flexShrink: 0 }} />
              <div style={{ fontSize: "clamp(7px,0.62vw,9px)", fontFamily: FONT_MONO, color: accent, letterSpacing: 1.5, textTransform: "uppercase", textAlign: "center", marginBottom: 4 }}>
                {j.stage}
              </div>
              <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(9px,0.82vw,12px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.5, color: "#9bbdd6", margin: 0, textAlign: "center" }}>
                {j.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Healing callout */}
      <div style={{ background: "rgba(155,114,221,0.06)", border: "1px solid rgba(155,114,221,0.20)", borderRadius: 8, padding: "clamp(9px,1.1vw,15px) clamp(11px,1.3vw,18px)" }}>
        <div style={lbl}>{meta.opportunityLabel}</div>
        <p style={{ ...txt, color: "#d0c0f8" }}>{renderSegs(opportunity, "#ffffff")}</p>
      </div>

    </div>

    {/* Panel — Memory Depth canvas fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <MemoryDepthCanvas />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide5: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide5Mobile />
    : <ScaleToFit><HTLDSlide5Inner /></ScaleToFit>;
};

export default HTLDSlide5;